import React, { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';
import { Box, Grid, Typography, useTheme } from '@mui/material';

import { CCTable, CrystalButton } from 'components';
import { useAppDispatch } from 'store/store.hook';
import { updateLayout } from 'store/slices/layout.slice';
import { ServiceInterruptionCreateDialog, ServiceInterruptionDeleteDialog } from 'dialogs/service-interruptions';
import { useServiceInterruptionsTable } from './useServiceInterruptionsTable';

export const ServiceInterruptionsPage = () => {
  const { width } = useWindowSize();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [
    { visibleServiceInterruptionCreateDialog, visibleServiceInterruptionDeleteDialog, serviceInterruptionId },
    setState,
  ] = useState<{
    visibleServiceInterruptionCreateDialog: boolean;
    visibleServiceInterruptionDeleteDialog: boolean;
    serviceInterruptionId?: number;
  }>({
    visibleServiceInterruptionCreateDialog: false,
    visibleServiceInterruptionDeleteDialog: false,
  });

  useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Service Interruptions' }));
  }, []);

  const setVisibleServiceInterruptionCreateDialog = (visibleServiceInterruptionCreateDialog: boolean) =>
    setState((prev) => ({ ...prev, visibleServiceInterruptionCreateDialog }));
  const setVisibleServiceInterruptionDeleteDialog = (visibleServiceInterruptionDeleteDialog: boolean) =>
    setState((prev) => ({ ...prev, visibleServiceInterruptionDeleteDialog }));
  const setServiceInterruptionId = (serviceInterruptionId?: number) =>
    setState((prev) => ({ ...prev, serviceInterruptionId }));
  const onDelete = (id: number) => {
    setServiceInterruptionId(id);
    setVisibleServiceInterruptionDeleteDialog(true);
  };

  const {
    data: { columns, rows },
    isLoading,
  } = useServiceInterruptionsTable({ onDelete });

  const descriptionText = (
    <>
      Service interruptions are comments on why service was not completed for a building or a specific zone. Comments
      will appear in the service zone report.
    </>
  );

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid item xs={12} container spacing={2} alignItems={'flex-start'}>
            <Grid item sx={{ flex: 1 }}>
              {width >= theme.breakpoints.values.sm && (
                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 500 }}>
                  {descriptionText}
                </Typography>
              )}
            </Grid>

            <Grid item>
              <CrystalButton onClick={() => setVisibleServiceInterruptionCreateDialog(true)} />
            </Grid>
          </Grid>

          {width < theme.breakpoints.values.sm && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 500 }}>
                {descriptionText}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <CCTable
              columns={columns}
              rows={rows}
              loading={{ isLoading, text: 'Loading service interruptions...' }}
              empty={{ description: 'No service interruptions yet' }}
            />
          </Grid>
        </Grid>
      </Box>

      <ServiceInterruptionCreateDialog
        visible={visibleServiceInterruptionCreateDialog}
        setVisible={setVisibleServiceInterruptionCreateDialog}
      />

      {serviceInterruptionId && (
        <ServiceInterruptionDeleteDialog
          serviceInterruptionId={serviceInterruptionId}
          setServiceInterruptionId={setServiceInterruptionId}
          visible={visibleServiceInterruptionDeleteDialog}
          setVisible={setVisibleServiceInterruptionDeleteDialog}
        />
      )}
    </>
  );
};
